<template>
  <div>
    <v-alert type="info" outlined dense border="left" class="mb-3">
      <div class="font-weight-bold">
        สรุปการรายงานผลการประเมิน (แบบ ปผ.พนร.) ปีงบประมาณ {{ year }} — ปีละ 2 รอบ
      </div>
      <div class="caption">
        รอบที่ 1: 1 ต.ค. {{ year - 1 }} – 31 มี.ค. {{ year }} · รอบที่ 2: 1 เม.ย. – 30 ก.ย. {{ year }}
        · ฐานรายชื่อ = ผู้ครองกรอบอัตรากำลัง + ผู้ที่มีในทะเบียนประวัติแต่ไม่อยู่ในกรอบ
        · บรรจุครั้งแรกหลังสิ้นรอบ/พ้นสภาพก่อนรอบ ไม่ต้องประเมินรอบนั้น · บรรจุระหว่างรอบต้องประเมิน · ผลประเมินผูกตามเลขบัตร (ย้ายแล้วนับที่สถานศึกษาใหม่)
      </div>
    </v-alert>

    <v-progress-linear v-if="loading" indeterminate color="primary" class="mb-2" />

    <template v-if="colleges.length">
      <!-- ภาพรวม -->
      <v-row dense class="mb-2">
        <v-col v-for="r in [1, 2]" :key="r" cols="12" md="6">
          <v-card outlined class="pa-3">
            <div class="subtitle-2 font-weight-bold mb-1">รอบที่ {{ r }}</div>
            <v-progress-linear
              :value="pct(totals['r' + r + '_assessed'], totals['r' + r + '_required'])"
              height="20"
              rounded
              :color="pct(totals['r' + r + '_assessed'], totals['r' + r + '_required']) >= 100 ? 'success' : 'primary'"
            >
              <span class="caption white--text font-weight-bold">
                รายงานแล้ว {{ fmt(totals['r' + r + '_assessed']) }} / {{ fmt(totals['r' + r + '_required']) }} คน
                ({{ pct(totals['r' + r + '_assessed'], totals['r' + r + '_required']).toFixed(1) }}%)
              </span>
            </v-progress-linear>
            <div class="d-flex flex-wrap mt-2" style="gap:4px">
              <v-chip x-small color="success" dark>อนุมัติ {{ fmt(totals['r' + r + '_approved']) }}</v-chip>
              <v-chip x-small color="info" dark>ส่งแล้ว {{ fmt(totals['r' + r + '_submitted']) }}</v-chip>
              <v-chip x-small color="grey" dark>ร่าง {{ fmt(totals['r' + r + '_draft']) }}</v-chip>
              <v-chip v-if="totals['r' + r + '_rejected']" x-small color="orange" dark>ส่งกลับ {{ fmt(totals['r' + r + '_rejected']) }}</v-chip>
              <v-chip x-small color="error" dark>ยังไม่รายงาน {{ fmt(totals['r' + r + '_missing']) }}</v-chip>
              <v-chip x-small outlined>บรรจุระหว่างรอบ {{ fmt(totals['r' + r + '_mid']) }}</v-chip>
              <v-chip x-small outlined>ไม่ต้องประเมิน (บรรจุหลังรอบ) {{ fmt(totals['r' + r + '_not_required']) }}</v-chip>
              <v-chip x-small outlined>พ้นสภาพ {{ fmt(totals['r' + r + '_left']) }}</v-chip>
              <v-chip v-if="totals['r' + r + '_not_due']" x-small outlined color="info">ยังไม่สิ้นรอบ {{ fmt(totals['r' + r + '_not_due']) }}</v-chip>
              <v-chip v-if="totals['r' + r + '_extra']" x-small outlined color="warning">มีผลประเมินทั้งที่ไม่ต้องประเมิน {{ fmt(totals['r' + r + '_extra']) }}</v-chip>
              <v-chip x-small outlined color="purple">ผลจากสถานศึกษาเดิม {{ fmt(totals['r' + r + '_from_other']) }}</v-chip>
              <v-chip x-small outlined color="error">
                สถานศึกษาครบ 100%: {{ fmt(completeColleges(r)) }} / {{ fmt(colleges.length) }} แห่ง
              </v-chip>
            </div>
          </v-card>
        </v-col>
      </v-row>
      <div class="d-flex flex-wrap mb-3" style="gap:4px">
        <v-chip small outlined>กรอบอัตรา {{ fmt(totals.frame_total) }} (ว่าง {{ fmt(totals.frame_vacant) }})</v-chip>
        <v-chip small outlined>ผู้ครองกรอบ {{ fmt(totals.frame_filled) }}</v-chip>
        <v-chip small outlined>ทะเบียนประวัติ {{ fmt(totals.registry_count) }}</v-chip>
        <v-chip small outlined color="warning">ไม่อยู่ในกรอบอัตรา {{ fmt(totals.not_in_frame) }}</v-chip>
        <v-chip small outlined color="warning">ไม่มีในทะเบียนประวัติ {{ fmt(totals.not_in_registry) }}</v-chip>
        <v-chip small outlined color="warning">ไม่มีวันที่บรรจุ {{ fmt(totals.no_appoint) }}</v-chip>
      </div>

      <v-row dense align="center" class="mb-1">
        <v-col cols="12" md="4">
          <v-text-field
            v-model="search"
            dense
            outlined
            hide-details
            clearable
            prepend-inner-icon="mdi-magnify"
            label="ค้นหาสถานศึกษา / จังหวัด / รหัส"
          />
        </v-col>
        <v-col cols="12" md="5">
          <v-btn-toggle v-model="filter" dense mandatory>
            <v-btn x-small value="all">ทั้งหมด</v-btn>
            <v-btn x-small value="r1">รอบ 1 ไม่ครบ</v-btn>
            <v-btn x-small value="r2">รอบ 2 ไม่ครบ</v-btn>
            <v-btn x-small value="issue">ข้อมูลกรอบ/ทะเบียนไม่ตรง</v-btn>
          </v-btn-toggle>
        </v-col>
        <v-col cols="12" md="3" class="text-right">
          <v-btn small color="success" :loading="exporting" @click="exportXlsx">
            <v-icon small left>mdi-microsoft-excel</v-icon>ส่งออก xlsx
          </v-btn>
        </v-col>
      </v-row>

      <v-data-table
        :headers="headers"
        :items="filtered"
        :search="search"
        dense
        item-key="college_code"
        :footer-props="{ itemsPerPageOptions: [20, 50, 100, -1] }"
        :items-per-page="20"
        class="elevation-0 overview-table"
      >
        <template v-slot:item.college_name="{ item }">
          <a class="font-weight-medium" @click="openPersons(item)">{{ item.college_name }}</a>
          <v-chip v-if="item.unit_type === 'C'" x-small outlined color="purple" class="ml-1">สถาบัน</v-chip>
          <div class="caption grey--text">{{ item.college_code }} · {{ item.province_name || "-" }}</div>
        </template>
        <template v-slot:item.frame_filled="{ item }">
          {{ item.frame_filled }}<span class="grey--text">/{{ item.frame_total }}</span>
          <div v-if="item.frame_vacant" class="caption grey--text">ว่าง {{ item.frame_vacant }}</div>
        </template>
        <template v-slot:item.registry_count="{ item }">
          <span :class="item.registry_diff !== 0 ? 'warning--text font-weight-bold' : ''">
            {{ item.registry_count }}
          </span>
          <div v-if="item.not_in_frame" class="caption warning--text">นอกกรอบ {{ item.not_in_frame }}</div>
          <div v-if="item.not_in_registry" class="caption warning--text">ไม่มีทะเบียน {{ item.not_in_registry }}</div>
        </template>
        <template v-for="r in [1, 2]" v-slot:[`item.r${r}_pct`]="{ item }">
          <div :key="r" style="min-width:150px">
            <v-progress-linear
              :value="item['r' + r + '_pct'] || 0"
              height="14"
              rounded
              :color="(item['r' + r + '_pct'] || 0) >= 100 ? 'success' : (item['r' + r + '_pct'] || 0) > 0 ? 'warning' : 'error'"
            >
              <span class="caption white--text">
                {{ item['r' + r + '_assessed'] }}/{{ item['r' + r + '_required'] }}
              </span>
            </v-progress-linear>
            <div class="caption">
              <span class="success--text">อนุมัติ {{ item['r' + r + '_approved'] }}</span>
              <span v-if="item['r' + r + '_missing']" class="error--text"> · ขาด {{ item['r' + r + '_missing'] }}</span>
              <span v-if="item['r' + r + '_mid']" class="grey--text"> · บรรจุระหว่างรอบ {{ item['r' + r + '_mid'] }}</span>
              <span v-if="item['r' + r + '_from_other']" class="purple--text"> · ย้ายมา {{ item['r' + r + '_from_other'] }}</span>
              <span v-if="item['r' + r + '_not_required']" class="grey--text"> · บรรจุหลังรอบ {{ item['r' + r + '_not_required'] }}</span>
              <span v-if="item['r' + r + '_extra']" class="warning--text"> · ประเมินเกิน {{ item['r' + r + '_extra'] }}</span>
            </div>
          </div>
        </template>
        <template v-slot:item.issue_persons="{ item }">
          <v-chip v-if="item.issue_persons" x-small color="warning" dark @click="openPersons(item)">
            {{ item.issue_persons }} คน
          </v-chip>
          <span v-else class="grey--text">-</span>
        </template>
      </v-data-table>
    </template>

    <!-- รายบุคคลของสถานศึกษา -->
    <v-dialog v-model="personDialog" max-width="1300" scrollable>
      <v-card v-if="personCollege">
        <v-card-title class="primary white--text py-2">
          <v-icon left dark>mdi-account-group</v-icon>
          {{ personCollege.college_name }}
          <span class="caption ml-2">ปีงบประมาณ {{ year }}</span>
          <v-spacer />
          <v-btn icon dark @click="personDialog = false"><v-icon>mdi-close</v-icon></v-btn>
        </v-card-title>
        <v-card-text class="pt-3">
          <v-data-table
            :headers="personHeaders"
            :items="persons"
            :loading="personLoading"
            dense
            disable-pagination
            hide-default-footer
            fixed-header
            height="520px"
          >
            <template v-slot:item.full_name="{ item }">
              <div class="font-weight-medium">{{ item.full_name || "(ไม่พบชื่อ)" }}</div>
              <div class="caption grey--text">{{ item.id_card }} · {{ item.name_postion || "-" }}</div>
            </template>
            <template v-for="r in [1, 2]" v-slot:[`item.r${r}_status`]="{ item }">
              <div :key="r">
                <v-chip x-small dark :color="statusColor(item['r' + r + '_status'])">
                  {{ statusText(item['r' + r + '_status']) }}
                </v-chip>
                <span v-if="item['r' + r + '_score'] !== null" class="caption ml-1">{{ item['r' + r + '_score'] }}</span>
                <div v-if="needNote(item, r)" class="caption grey--text">{{ needNote(item, r) }}</div>
                <div v-if="item['r' + r + '_need'] === 'mid'" class="caption grey--text">
                  บรรจุระหว่างรอบ ({{ item['r' + r + '_months'] }} เดือน)
                </div>
                <div v-if="+item['r' + r + '_from_other']" class="caption purple--text">
                  ผลจาก {{ item['r' + r + '_college_name'] }}
                </div>
              </div>
            </template>
            <template v-slot:item.contract_end="{ item }">
              <span v-if="item.contract_start || item.contract_end" class="caption">
                {{ item.contract_start || "?" }} – {{ item.contract_end || "?" }}
              </span>
              <span v-else class="caption grey--text">-</span>
              <div v-if="item.resignation_date" class="caption error--text">ลาออก {{ item.resignation_date }}</div>
            </template>
            <template v-slot:item.issues="{ item }">
              <span class="caption warning--text text--darken-2">{{ item.issues }}</span>
            </template>
          </v-data-table>
        </v-card-text>
      </v-card>
    </v-dialog>

    <v-snackbar v-model="snack.show" :color="snack.color" top>{{ snack.text }}</v-snackbar>
  </div>
</template>

<script>
import * as XLSX from "xlsx";

const API = "gov_em_assessment_overview.php";

const STATUS = {
  approved: ["อนุมัติ", "success"],
  submitted: ["ส่งแล้ว", "info"],
  draft: ["ร่าง", "grey"],
  rejected: ["ส่งกลับ", "orange"],
  missing: ["ยังไม่รายงาน", "error"],
  not_due: ["ยังไม่สิ้นรอบ", "info"],
  not_required: ["ไม่ต้องประเมิน", "blue-grey lighten-2"]
};

export default {
  name: "AssessmentRoundOverview",
  props: {
    year: { type: Number, required: true },
    collegeCode: { type: String, default: null }
  },

  data() {
    return {
      ApiKey: "HRvec2021",
      loading: false,
      exporting: false,
      colleges: [],
      search: "",
      filter: "all",
      personDialog: false,
      personLoading: false,
      personCollege: null,
      persons: [],
      snack: { show: false, color: "error", text: "" },
      headers: [
        { text: "สถานศึกษา", value: "college_name" },
        { text: "ภาค", value: "region_name" },
        { text: "ครองกรอบ/กรอบ", value: "frame_filled", align: "center" },
        { text: "ทะเบียนประวัติ", value: "registry_count", align: "center" },
        { text: "รอบที่ 1 (รายงาน/ต้องประเมิน)", value: "r1_pct" },
        { text: "รอบที่ 2 (รายงาน/ต้องประเมิน)", value: "r2_pct" },
        { text: "ต้องตรวจข้อมูล", value: "issue_persons", align: "center" },
        { text: "", value: "province_name", align: " d-none" },
        { text: "", value: "college_code", align: " d-none" }
      ],
      personHeaders: [
        { text: "ชื่อ-สกุล", value: "full_name" },
        { text: "เลขที่ตำแหน่ง", value: "id_position" },
        { text: "วันที่บรรจุครั้งแรก", value: "appoint_date" },
        { text: "สัญญาจ้าง", value: "contract_end" },
        { text: "รอบที่ 1", value: "r1_status" },
        { text: "รอบที่ 2", value: "r2_status" },
        { text: "ต้องตรวจข้อมูล", value: "issues" }
      ]
    };
  },

  computed: {
    scoped() {
      return this.collegeCode ? this.colleges.filter(c => c.college_code === this.collegeCode) : this.colleges;
    },
    filtered() {
      const list = this.scoped;
      if (this.filter === "r1") return list.filter(c => c.r1_missing > 0);
      if (this.filter === "r2") return list.filter(c => c.r2_missing > 0);
      if (this.filter === "issue") return list.filter(c => c.issue_persons > 0 || c.registry_diff !== 0);
      return list;
    },
    totals() {
      const t = {};
      this.scoped.forEach(c =>
        Object.keys(c).forEach(k => {
          if (typeof c[k] === "number") t[k] = (t[k] || 0) + c[k];
        })
      );
      return t;
    }
  },

  watch: {
    year() {
      this.load();
    }
  },

  mounted() {
    this.load();
  },

  methods: {
    fmt(v) {
      return Number(v || 0).toLocaleString("th-TH");
    },
    pct(a, b) {
      return b ? (a / b) * 100 : 0;
    },
    completeColleges(r) {
      return this.scoped.filter(c => c["r" + r + "_missing"] === 0).length;
    },
    needNote(p, r) {
      const need = p["r" + r + "_need"];
      if (need === "not_required") return "บรรจุหลังสิ้นรอบ";
      if (need === "left_before") return "พ้นสภาพก่อนรอบ " + (p.resignation_date || "");
      if (need === "left_mid") return "พ้นสภาพระหว่างรอบ " + (p.resignation_date || "") + " (ไม่บังคับ)";
      if (need === "unknown") return "ไม่มีวันที่บรรจุ";
      return "";
    },
    statusText(s) {
      return (STATUS[s] || [s])[0];
    },
    statusColor(s) {
      return (STATUS[s] || [s, "grey"])[1];
    },
    async post(payload) {
      try {
        const res = await this.$http.post(API, { ApiKey: this.ApiKey, comp_year: this.year, ...payload });
        if (!res.data.status) throw new Error(res.data.message);
        return res.data;
      } catch (e) {
        const msg = (e.response && e.response.data && e.response.data.message) || e.message;
        this.snack = { show: true, color: "error", text: msg };
        throw e;
      }
    },
    async load() {
      this.loading = true;
      try {
        this.colleges = (await this.post({ action: "overview" })).colleges;
        this.$emit("loaded", this.colleges);
      } catch (e) {
        this.colleges = [];
      } finally {
        this.loading = false;
      }
    },
    async openPersons(college) {
      this.personCollege = college;
      this.persons = [];
      this.personDialog = true;
      this.personLoading = true;
      try {
        this.persons = (await this.post({ action: "persons", college_code: college.college_code })).persons;
      } catch (e) {
        this.persons = [];
      } finally {
        this.personLoading = false;
      }
    },
    async exportXlsx() {
      this.exporting = true;
      try {
        const persons = (await this.post({ action: "persons", college_code: this.collegeCode || undefined })).persons;
        const wb = XLSX.utils.book_new();
        const collegeRows = this.filtered.map((c, i) => ({
          ลำดับ: i + 1,
          รหัส: c.college_code,
          สถานศึกษา: c.college_name,
          ประเภท: c.unit_type === "C" ? "สถาบัน" : c.unit_type === "V" ? "ส่วนกลาง" : "สถานศึกษา",
          จังหวัด: c.province_name,
          ภาค: c.region_name,
          กรอบอัตรา: c.frame_total,
          ผู้ครองกรอบ: c.frame_filled,
          ตำแหน่งว่าง: c.frame_vacant,
          ทะเบียนประวัติ: c.registry_count,
          "ทะเบียน-กรอบ": c.registry_diff,
          ไม่อยู่ในกรอบ: c.not_in_frame,
          ไม่มีทะเบียนประวัติ: c.not_in_registry,
          ไม่มีวันที่บรรจุ: c.no_appoint,
          "ร1 ต้องประเมิน": c.r1_required,
          "ร1 บรรจุระหว่างรอบ": c.r1_mid,
          "ร1 ไม่ต้องประเมิน (บรรจุหลังรอบ)": c.r1_not_required,
          "ร1 พ้นสภาพ": c.r1_left,
          "ร1 ยังไม่สิ้นรอบ": c.r1_not_due,
          "ร1 ประเมินเกิน": c.r1_extra,
          "ร1 รายงานแล้ว": c.r1_assessed,
          "ร1 อนุมัติ": c.r1_approved,
          "ร1 ส่งแล้ว": c.r1_submitted,
          "ร1 ร่าง": c.r1_draft,
          "ร1 ยังไม่รายงาน": c.r1_missing,
          "ร1 ผลจากสถานศึกษาเดิม": c.r1_from_other,
          "ร1 %": c.r1_pct,
          "ร2 ต้องประเมิน": c.r2_required,
          "ร2 บรรจุระหว่างรอบ": c.r2_mid,
          "ร2 ไม่ต้องประเมิน (บรรจุหลังรอบ)": c.r2_not_required,
          "ร2 พ้นสภาพ": c.r2_left,
          "ร2 ยังไม่สิ้นรอบ": c.r2_not_due,
          "ร2 ประเมินเกิน": c.r2_extra,
          "ร2 รายงานแล้ว": c.r2_assessed,
          "ร2 อนุมัติ": c.r2_approved,
          "ร2 ส่งแล้ว": c.r2_submitted,
          "ร2 ร่าง": c.r2_draft,
          "ร2 ยังไม่รายงาน": c.r2_missing,
          "ร2 ผลจากสถานศึกษาเดิม": c.r2_from_other,
          "ร2 %": c.r2_pct,
          ต้องตรวจข้อมูล: c.issue_persons
        }));
        XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(collegeRows), "สรุปรายสถานศึกษา");
        const personRows = persons.map((p, i) => ({
          ลำดับ: i + 1,
          รหัสสถานศึกษา: p.college_code,
          สถานศึกษา: p.college_name,
          เลขบัตรประชาชน: p.id_card,
          "ชื่อ-สกุล": p.full_name,
          เลขที่ตำแหน่ง: p.id_position || "",
          ตำแหน่ง: p.name_postion || "",
          วันที่บรรจุครั้งแรก: p.appoint_date || "",
          เริ่มสัญญา: p.contract_start || "",
          สิ้นสุดสัญญา: p.contract_end || "",
          วันที่ลาออก: p.resignation_date || "",
          "ร1 ความจำเป็น": this.needNote(p, 1) || "ต้องประเมิน",
          "ร2 ความจำเป็น": this.needNote(p, 2) || "ต้องประเมิน",
          "ร1 สถานะ": this.statusText(p.r1_status),
          "ร1 คะแนน": p.r1_score,
          "ร1 ประเมินโดย": p.r1_college_name || "",
          "ร1 บรรจุระหว่างรอบ (เดือน)": p.r1_months === null ? "" : p.r1_months,
          "ร2 สถานะ": this.statusText(p.r2_status),
          "ร2 คะแนน": p.r2_score,
          "ร2 ประเมินโดย": p.r2_college_name || "",
          "ร2 บรรจุระหว่างรอบ (เดือน)": p.r2_months === null ? "" : p.r2_months,
          ต้องตรวจข้อมูล: p.issues
        }));
        XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(personRows), "รายบุคคล");
        XLSX.writeFile(wb, `สรุปการประเมิน_ปผพนร_${this.year}.xlsx`);
      } catch (e) {
        // แจ้งใน post() แล้ว
      } finally {
        this.exporting = false;
      }
    }
  }
};
</script>

<style scoped>
.overview-table >>> td {
  vertical-align: top;
  padding-top: 4px !important;
  padding-bottom: 4px !important;
}
</style>
